package com.sweetlime.dungeonamigo.presentation.features.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.presentation.navigation.MainScreen
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.state.AuthState
import org.koin.compose.koinInject

class AuthScreen : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: AuthViewModel = koinInject()
        val state by vm.state.collectAsState()
        var email by remember { mutableStateOf("") }
        var password by remember { mutableStateOf("") }
        var isLogin by remember { mutableStateOf(true) }

        LaunchedEffect(state) {
            if (state is AuthState.Success) navigator.replace(MainScreen)
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(BrandColors.Background)
                .padding(horizontal = 32.dp),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text("🐉", fontSize = 60.sp)
            Spacer(Modifier.height(8.dp))
            Text(
                "Dungeon Amigo",
                color = BrandColors.Parchment,
                fontWeight = FontWeight.Bold,
                fontSize = 28.sp,
            )
            Text(
                if (isLogin) "Welcome back, adventurer" else "Create your legend",
                color = BrandColors.TextSecondary,
                fontSize = 14.sp,
            )
            Spacer(Modifier.height(40.dp))

            OutlinedTextField(
                value = email,
                onValueChange = { email = it },
                label = { Text("Email", color = BrandColors.TextSecondary) },
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = BrandColors.Ember,
                    unfocusedBorderColor = BrandColors.TextSecondary,
                    focusedTextColor = BrandColors.TextPrimary,
                    unfocusedTextColor = BrandColors.TextPrimary,
                    cursorColor = BrandColors.Ember,
                ),
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(12.dp))
            OutlinedTextField(
                value = password,
                onValueChange = { password = it },
                label = { Text("Password", color = BrandColors.TextSecondary) },
                singleLine = true,
                visualTransformation = PasswordVisualTransformation(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = BrandColors.Ember,
                    unfocusedBorderColor = BrandColors.TextSecondary,
                    focusedTextColor = BrandColors.TextPrimary,
                    unfocusedTextColor = BrandColors.TextPrimary,
                    cursorColor = BrandColors.Ember,
                ),
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(24.dp))

            if (state is AuthState.Error) {
                Text(
                    (state as AuthState.Error).message,
                    color = BrandColors.Error,
                    fontSize = 13.sp,
                )
                Spacer(Modifier.height(8.dp))
            }

            Button(
                onClick = { if (isLogin) vm.login(email, password) else vm.signup(email, password) },
                enabled = state !is AuthState.Loading,
                modifier = Modifier.fillMaxWidth().height(50.dp),
                colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
            ) {
                if (state is AuthState.Loading) {
                    CircularProgressIndicator(color = BrandColors.TextPrimary, modifier = Modifier.size(20.dp))
                } else {
                    Text(if (isLogin) "Enter the Dungeon" else "Begin Your Quest", fontWeight = FontWeight.Bold)
                }
            }
            Spacer(Modifier.height(16.dp))
            TextButton(onClick = { isLogin = !isLogin }) {
                Text(
                    if (isLogin) "New adventurer? Create account" else "Already have an account? Sign in",
                    color = BrandColors.Parchment,
                )
            }
        }
    }
}
